import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import useTargetMatrix from './useTargetMatrix';
import useScheduledServicesToday from './useScheduledServicesToday';
import { Container, Typography, Box, TextField, MenuItem, Button, Alert, Stack } from '@mui/material';

const brandGreen = '#3a7d3b';
const brandDarkGreen = '#27632a';
const accentYellow = '#ffe066';
const fontFamily = 'Montserrat, Open Sans, Arial, sans-serif';

const TRUCK_STORAGE_KEY = 'selectedTruckId';
const TECH_STORAGE_KEY = 'selectedTechId';

const API_URL = process.env.REACT_APP_API_URL || '';
console.log("API_URL used:", API_URL);

export default function TechnicianForm() {
  const [submitting, setSubmitting] = useState(false);
  // State to trigger reload of scheduled services
  const [reloadScheduledServices, setReloadScheduledServices] = useState(0);
  // State for fields to reset after submission
  const initialFormState = {
    selectedCustomerKey: '',
    quantity: '',
    notes: '',
    target: null,
    variables: {},
    serviceCodeMatch: null,
    lastSubmissionData: null,
    submissionSuccess: '',
    result: null,
  };
  const [formState, setFormState] = useState(initialFormState);

  // Truck and technician state (not reset after submission)
  const [trucks, setTrucks] = useState([]);
  const { matrix, serviceCodes, getTarget, loading: targetMatrixLoading, error: targetMatrixError } = useTargetMatrix();
  const [truckId, setTruckId] = useState('');
  const [truckLocked, setTruckLocked] = useState(false);
  const [techs, setTechs] = useState([]);
  const [techId, setTechId] = useState('');
  const [techLocked, setTechLocked] = useState(false);
  const [flashColor, setFlashColor] = useState('');
  const [submissionError, setSubmissionError] = useState('');

  // Get scheduled services today
  // Always read override state from localStorage, not UI
const showAllCustomers = localStorage.getItem('showAllCustomers') === 'true';
const { scheduledServicesToday } = useScheduledServicesToday(undefined, undefined, reloadScheduledServices, showAllCustomers);

  // Memoized customer options sorted by startDate and scheduledTime
  const customerOptions = useMemo(() => {
  // Sort by customer first name only, ascending
  return (scheduledServicesToday || []).slice().sort((a, b) => {
    const nameA = (a.firstName || '').toLowerCase();
    const nameB = (b.firstName || '').toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });
}, [scheduledServicesToday]);

  // Find the selected customer (scheduled service)
  const selectedCustomer = customerOptions.find(
    (c) => c.programID === formState.selectedCustomerKey
  );

  // Service code for logic: use the selected scheduled service's serviceCode
  const selectedServiceCode = selectedCustomer ? selectedCustomer.serviceCode : '';

  useEffect(() => {
    // Fetch trucks from backend cache (updated daily)
    axios.get(`${API_URL}/api/trucks`).then(r => {
      setTrucks(r.data);
    }).catch(err => {
      console.error('Failed to fetch trucks:', err);
      setTrucks([]);
    });
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/technicians`).then(r => setTechs(r.data));
  }, []);

  useEffect(() => {
    const savedTruck = localStorage.getItem(TRUCK_STORAGE_KEY);
    if (savedTruck) {
      setTruckId(savedTruck);
      setTruckLocked(true);
    }
  }, []);

  useEffect(() => {
    const savedTech = localStorage.getItem(TECH_STORAGE_KEY);
    if (savedTech) {
      setTechId(savedTech);
      setTechLocked(true);
    }
  }, []);

  useEffect(() => {
    if (truckId && !truckLocked) {
      localStorage.setItem(TRUCK_STORAGE_KEY, truckId);
      setTruckLocked(true);
    }
  }, [truckId, truckLocked]);

  useEffect(() => {
    if (techId && !techLocked) {
      localStorage.setItem(TECH_STORAGE_KEY, techId);
      setTechLocked(true);
    }
  }, [techId, techLocked]);

  const handleChangeTruck = () => {
    localStorage.removeItem(TRUCK_STORAGE_KEY);
    setTruckId('');
    setTruckLocked(false);
    setServiceId('');
    setQuantity('');
    setTarget(null);
  };

  const handleChangeTech = () => {
    localStorage.removeItem(TECH_STORAGE_KEY);
    setTechId('');
    setTechLocked(false);
  };

  const handleSubmit = async (e) => {
    if (submitting) return; // Prevent double submit
    setSubmitting(true);
    e.preventDefault();
    setFormState(f => ({ ...f, result: null, submissionError: '', submissionSuccess: '' }));
    // Pull size from selected scheduled service
    const size = selectedCustomer ? selectedCustomer.size : undefined;
    const lookedUpTarget = getTarget(size, selectedServiceCode);
    console.log('[DEBUG] handleSubmit:', { selectedServiceCode, size, lookedUpTarget, quantity: formState.quantity });
    if (typeof lookedUpTarget !== 'number' || isNaN(lookedUpTarget) || lookedUpTarget === null) {
      setFormState(f => ({ ...f, submissionError: 'Target quantity could not be determined for this service and size. Please check your selections.', submissionSuccess: '' }));
      return;
    }
    // Find the selected technician's full name
    const selectedTech = techs.find(t => t._id === techId);
    const technicianName = selectedTech ? `${selectedTech.firstName} ${selectedTech.lastName}` : '';
    const submissionData = {
      technician: techId,
      technicianName,
      customerNumber: selectedCustomer?.customerNumber || '',
      customerFirstName: selectedCustomer?.firstName || '',
      customerLastName: selectedCustomer?.lastName || '',
      programID: selectedCustomer?.programID || '',
      truck: truckId.trim(),
      service: selectedServiceCode, // Use the selected service code as 'service' for backend
      size: String(size),
      productUsed: Number(formState.quantity),
      target: lookedUpTarget,
      notes: formState.notes.trim(),
      email: selectedCustomer?.preferredEmail || '', // for backend sync only
    };

    console.log('SUBMISSION PAYLOAD', submissionData);
    if (typeof lookedUpTarget !== 'number' || isNaN(lookedUpTarget)) {
      setFormState(f => ({ ...f, submissionError: 'No valid target found for this service. Please verify your selection.' }));
      return;
    }
    let saved = false;
    try {
      saved = await saveSubmission(submissionData);
    } catch (err) {
      setSubmitting(false);
      throw err;
    }
    console.log('[DEBUG] saveSubmission result:', saved);
    if (saved) {
      let color = '';
      const actual = Number(formState.quantity);
      const tgt = Number(lookedUpTarget);
      let score = 100;
      if (tgt == null || tgt === 0) {
        score = 100;
      } else if (tgt > 0) {
        score = Math.max(0, 100 - (Math.abs(actual - tgt) / tgt) * 100);
        score = Math.round(score);
      }
      if (score >= 90) color = '#43a047';
      else if (score >= 80) color = '#fbc02d';
      else if (score >= 70) color = '#fb8c00';
      else color = '#e53935';
      setFlashColor(color);
      setFormState(f => ({ ...f, result: { score } }));
      setTimeout(() => {
        setFlashColor('');
        setFormState(initialFormState);
        setSubmitting(false);
      }, 1500);
    }
  };


  const handleRetry = async () => {
    if (lastSubmissionData) {
      setSubmissionError('');
      setSubmissionSuccess('');
      const saved = await saveSubmission(lastSubmissionData);
      if (saved) {
        setLastSubmissionData(null);
      }
    }
  };

  const saveSubmission = async (submissionData) => {
    try {
      await axios.post(`${API_URL}/api/submissions`, submissionData);
      // Trigger reload of scheduled services
      setReloadScheduledServices(r => r + 1);
      // Trigger HubSpot sync if notes are present and email is available
      if (submissionData.notes && submissionData.email) {
        try {
          await axios.post(`${API_URL}/api/hubspot-task`, {
            email: submissionData.email,
            note: submissionData.notes,
            customerNumber: submissionData.customerNumber,
            service: submissionData.service,
          });
        } catch (err) {
          console.error('Failed to sync with HubSpot:', err);
        }
      }
      setFormState(f => ({ ...f, submissionError: '', submissionSuccess: 'Submission saved successfully!', lastSubmissionData: null }));
      return true;
    } catch (err) {
      setFormState(f => ({ ...f, submissionSuccess: '', submissionError: 'Failed to save submission. Please check your connection and try again.', lastSubmissionData: submissionData }));
      return false;
    }
  };



  return (
    <Container maxWidth="sm" sx={{
      mt: 4, mb: 4,
      px: { xs: 2, sm: 4, md: 6 },
      py: { xs: 3, sm: 5 },
      borderRadius: { xs: 0, sm: 4 },
      boxShadow: { xs: 0, sm: 4 },
      background: flashColor ? flashColor : 'white',
      fontFamily,
      minHeight: { xs: '90vh', sm: '80vh' },
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      border: { xs: 'none', sm: `2px solid ${brandGreen}` },
    }}>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: brandGreen,
          mb: 3,
          textAlign: 'center',
          letterSpacing: 1,
          fontFamily,
        }}
      >
        ServiSync Field App
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>

        {truckLocked ? (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexDirection: { xs: 'row', sm: 'row' }, justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                background: brandGreen,
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                minWidth: 0,
                px: 2,
                mr: 2,
                '&:hover': { background: brandDarkGreen },
                alignSelf: 'flex-start',
                fontSize: { xs: 14, sm: 16 }
              }}
              onClick={handleChangeTruck}
            >Change Truck</Button>
            <Typography sx={{ fontWeight: 700, color: brandGreen, fontSize: { xs: 18, sm: 20 } }}>
              Truck: {trucks.find(t => t.id === truckId)?.name || ''}
            </Typography>
          </Stack>
        ) : (
          <TextField
            select
            fullWidth
            label="Select Truck"
            value={truckId}
            onChange={e => setTruckId(e.target.value)}
            margin="normal"
            size="large"
            InputProps={{ style: { minHeight: 56, fontSize: 18 } }}
          >
            {trucks.map(truck => (
              <MenuItem key={truck.id} value={truck.id}>{truck.name}</MenuItem>
            ))}
          </TextField>
        )}

        {techLocked ? (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexDirection: { xs: 'row', sm: 'row' }, justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                background: brandGreen,
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                minWidth: 0,
                px: 2,
                mr: 2,
                '&:hover': { background: brandDarkGreen },
                alignSelf: 'flex-start',
                fontSize: { xs: 14, sm: 16 }
              }}
              onClick={handleChangeTech}
            >Change Technician</Button>
            <Typography sx={{ fontWeight: 700, color: brandGreen, fontSize: { xs: 18, sm: 20 } }}>
              Technician: {(techs.find(t => t._id === techId) ? `${techs.find(t => t._id === techId).firstName} ${techs.find(t => t._id === techId).lastName}` : '')}
            </Typography>
          </Stack>
        ) : (
          <TextField
            select
            fullWidth
            label="Select Technician"
            value={techId}
            onChange={e => setTechId(e.target.value)}
            margin="normal"
            size="large"
            InputProps={{ style: { minHeight: 56, fontSize: 18 } }}
          >
            {techs.filter(tech => tech.visible !== false).map(tech => (
              <MenuItem key={tech._id} value={tech._id}>{tech.firstName} {tech.lastName}</MenuItem>
            ))}
          </TextField>
        )}

      <TextField
        select
        fullWidth
        label="Select Customer"
        value={formState.selectedCustomerKey}
        onChange={e => setFormState(f => ({ ...f, selectedCustomerKey: e.target.value }))}
        margin="normal"
        size="large"
        InputProps={{ style: { minHeight: 56, fontSize: 18 } }}
      >
        <MenuItem value="" disabled>
          Select a customer
        </MenuItem>
        {customerOptions.map((cust, idx) => {
  const displayName = cust.customerFullName || cust.companyName || '(Unknown Customer)';
  return (
    <MenuItem key={cust.programID} value={cust.programID}>
      {`${displayName} - ${cust.serviceCode}`}
    </MenuItem>
  );
})}
      </TextField>
      {selectedCustomer && (
        <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: 16 }}>
          Address: {selectedCustomer.addressLine1}
        </Typography>
        )}
        <TextField
          fullWidth
          label="Enter Quantity Used"
          type="number"
          value={formState.quantity}
          onChange={e => setFormState(f => ({ ...f, quantity: e.target.value }))}
          margin="normal"
          disabled={!selectedCustomer}
          size="large"
          placeholder="Enter Quantity Used"
          InputProps={{ style: { minHeight: 56, fontSize: 18 } }}
        />
        <TextField
          fullWidth
          label="Service Notes"
          multiline
          minRows={3}
          maxRows={8}
          value={formState.notes}
          onChange={e => setFormState(f => ({ ...f, notes: e.target.value }))}
          margin="normal"
          size="large"
          InputProps={{ style: { minHeight: 56, fontSize: 18 } }}
          placeholder="Add any notes or details about this service stop..."
        />

      {/* END technician section */}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        sx={{
          mt: 2, minHeight: 56, fontSize: 20, borderRadius: 2, touchAction: 'manipulation',
          background: brandGreen, color: 'white', fontWeight: 700,
          '&:hover': { background: brandDarkGreen }
        }}
        disabled={!truckId || !selectedCustomer || !formState.quantity || submitting}
      >
        Submit
      </Button>

      </Box>
      {formState.submissionError && (
        <Alert severity="error" sx={{ mt: 2 }}
          action={lastSubmissionData ? <Button color="inherit" size="small" onClick={handleRetry}>Retry</Button> : null}
        >{formState.submissionError}</Alert>
      )}
      {formState.submissionSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>{formState.submissionSuccess}</Alert>
      )}
      {formState.result && (
        <Alert sx={{ mt: 2, fontSize: { xs: '1rem', sm: '1.2rem' } }} severity="success">
          Submitted! Score: <b>{formState.result.score}</b>
        </Alert>
      )}
    </Container>
  );
}
